import * as Sentry from "@sentry/browser";
import moment from "moment";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { MobileInvite, NewUser, User } from "sweet-global";
import firebase from "sweet-web/firebase";
import { logIn } from "sweet-web/redux/app/actions";
import { Auth } from "sweet-web/redux/auth/actions";
import { RootState } from "../index";
import {
  CREATE_USER_FAILURE,
  CREATE_USER_INIT,
  CREATE_USER_SUCCESS,
  GET_USER_FAILURE,
  GET_USER_INIT,
  GET_USER_SUCCESS,
  RESET_USER,
  UPDATE_USER_FAILURE,
  UPDATE_USER_INIT,
  UPDATE_USER_SUCCESS,
  UserActionTypes
} from "./types";

const firestore = firebase.firestore();
const storage = firebase.storage();

export function resetUser(): UserActionTypes {
  return { type: RESET_USER };
}

export function updateUserInit(): UserActionTypes {
  return { type: UPDATE_USER_INIT };
}

export function updateUserSuccess(user: User): UserActionTypes {
  return { type: UPDATE_USER_SUCCESS, user };
}

export function updateUserFailure(error: any): UserActionTypes {
  return { type: UPDATE_USER_FAILURE, error };
}

export function getUserInit(): UserActionTypes {
  return { type: GET_USER_INIT };
}

export function getUserSuccess(data: User): UserActionTypes {
  return { type: GET_USER_SUCCESS, data };
}

export function getUserFailure(error: any): UserActionTypes {
  return { type: GET_USER_FAILURE, error };
}

export function createUserInit(): UserActionTypes {
  return { type: CREATE_USER_INIT };
}

export function createUserSuccess(user: User): UserActionTypes {
  return { type: CREATE_USER_SUCCESS, user };
}

export function createUserFailure(error: any): UserActionTypes {
  return { type: CREATE_USER_FAILURE, error };
}

export const GetUser = (): ThunkAction<
  Promise<User>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    const state = getState();
    dispatch(getUserInit());

    if (!state.auth.uid) throw new Error("not authorized");

    const userDoc = await firestore
      .collection("users")
      .doc(state.auth.uid)
      .get();

    const userData = userDoc.data() as User;

    if (userData) dispatch(getUserSuccess(userData));

    return userData;
  } catch (error) {
    Sentry.captureException(error);
    dispatch(getUserFailure(error));
  }
};

export const UpdateUser = (
  data: Partial<User>
): ThunkAction<void, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const state = getState();

    dispatch(updateUserInit());

    if (!state.auth.uid) throw new Error("not authorized");

    await firestore
      .collection("users")
      .doc(state.auth.uid)
      .update(data);

    const userDoc = await firestore
      .collection("users")
      .doc(state.auth.uid)
      .get();

    const userData = userDoc.data() as User;

    dispatch(updateUserSuccess(userData));
  } catch (error) {
    dispatch(updateUserFailure(error));
    Sentry.captureException(error);
  }
};

export const CreateUser = (
  data: NewUser,
  password: string
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    dispatch(createUserInit());

    const uid = await Auth(data.emailAddress, password, true)(
      dispatch,
      getState,
      null
    );

    if (!uid) {
      throw new Error("Could not create user. Try signing in instead");
    }

    const userData: User = {
      ...data,
      joinedTime: moment().unix()
    };
    await firestore
      .collection("users")
      .doc(uid)
      .set(userData, { merge: true });

    dispatch(createUserSuccess(userData));
    dispatch(logIn());
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    dispatch(createUserFailure(error));
  }
};

export const UploadUserImage = (
  file: File
): ThunkAction<
  Promise<string | null>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    const state = getState();

    if (!state.auth.uid) throw new Error("no authenticated user");

    const userImageRef = storage.ref(`userImages/${state.auth.uid}`);

    await userImageRef.put(file);

    const url = await userImageRef.getDownloadURL();

    await UpdateUser({ image: url })(dispatch, getState, null);
    if (typeof url == "string") return url as string;
    else throw new Error("Could not upload user image");
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);

    return null;
  }
};

export const SendMobileInvite = (
  phoneNumber: string,
  venueId?: string
): ThunkAction<Promise<boolean>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const state = getState();

    if (!phoneNumber) throw new Error("no phone number");

    const invite: MobileInvite = {
      phoneNumber,
      time: moment().unix()
    };

    if (state.user.entity.type == "VENUE" && venueId) {
      invite.venueId = venueId;
    }

    const invited = await firestore.collection("invites").add(invite);
    if (invited) return true;
    else return false;
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);

    return false;
  }
};

export const GetUserById = (
  userId: string
): ThunkAction<Promise<User | null>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const userDoc = await firestore
      .collection("users")
      .doc(userId)
      .get();
    if (!userDoc.exists) return null;

    return {
      id: userDoc.id,
      ...userDoc.data()
    } as User;
  } catch (error) {
    console.log(error);
    return null;
  }
};
